import t from "@/lang/lang";
import { PestKind } from "@/services/backend/qpm/pests/types/pest-enum";
import {
  MonitorSnapDTO,
  PestSnapDTO,
} from "@/services/backend/qpm/monitors/types/monitor-snapshot";
import { MonitorPestView } from "@/services/backend/qpm/monitors/types/monitor-view";
import {
  PestSelectorComponent,
  SelectedPestsTable,
} from "@/routes/base-data/processes/qpm/monitors/[monitorId]/_components/edit-monitor-pest-category";
import { useQpmCatalogItemSetPestsMutation } from "@/services/backend/qpm/catalog/api/catalog-api";

export function CatalogMonitorPestsComponent({
  catalogItemId,
  monitor,
  readOnly,
}: {
  catalogItemId: string;
  monitor: MonitorSnapDTO;
  readOnly?: boolean;
}) {
  const [replace] = useQpmCatalogItemSetPestsMutation();

  // Some parts of the codebase still type `monitor.pests` inconsistently.
  // Normalize it here to the shape the replace endpoint expects.
  const pests = ((monitor.pests ?? []) as unknown as PestSnapDTO[])
    .map((p) => ({
      id: p.sourceId,
      kind: p.kind,
      name: p.name,
    }))
    .filter((p) => !!p.id);

  const toSnapshot = (p: MonitorPestView): PestSnapDTO => {
    const now = new Date().toISOString();
    return {
      sourceId: p.id,
      key: "",
      kind: p.kind as PestKind,
      name: p.name,
      abbreviation: "",
      description: "",
      synonyms: [],
      createdAt: now,
      updatedAt: now,
      takenAt: now,
    };
  };

  const update = (newList: MonitorPestView[]) => {
    if (readOnly) return;
    replace({
      catalogItemId,
      pests: newList.map(toSnapshot),
    });
  };

  const add = (p: MonitorPestView) => {
    if (readOnly) return;
    if (pests.some((x) => x.id === p.id && x.kind === p.kind)) return;
    update([...pests, p]);
  };

  const remove = (kind: PestKind, id: string) => {
    if (readOnly) return;
    update(pests.filter((p) => !(p.kind === kind && p.id === id)));
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="text-sm font-semibold">{t("Schädlinge")}</div>
      <div className="grid items-start gap-6 md:grid-cols-2">
        <div className="flex flex-col gap-2">
          <div className="text-sm font-medium">{t("Kategorien")}</div>
          <PestSelectorComponent
            kind={PestKind.Category}
            selectedItems={pests}
            onSelect={add}
            disabled={readOnly}
          />
          <SelectedPestsTable
            kind={PestKind.Category}
            selectedItems={pests}
            onDeleteSelected={(id) => remove(PestKind.Category, id)}
            disabled={readOnly}
          />
        </div>
        <div className="flex flex-col gap-2">
          <div className="text-sm font-medium">{t("Indikatoren")}</div>
          <PestSelectorComponent
            kind={PestKind.Indicator}
            selectedItems={pests}
            onSelect={add}
            disabled={readOnly}
          />
          <SelectedPestsTable
            kind={PestKind.Indicator}
            selectedItems={pests}
            onDeleteSelected={(id) => remove(PestKind.Indicator, id)}
            disabled={readOnly}
          />
        </div>
      </div>
    </div>
  );
}
